import { useState, useEffect } from 'react'
import { ChevronRight, ChevronDown, ChevronLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { FAQSection } from '../components/FAQSection'
import { DepositModal } from '../components/DepositModal'
import { TransactionSuccessModal } from '../components/TransactionSuccessModal'
import { CommonButton } from '../components/CommonButton'
import { Phase2TestPanel } from '../components/dev/Phase2TestPanel'
import { Toast } from '../components/Toast'
import { getPhase2Data, formatNumberWithCommas, ApiError } from '../lib/api'
import { useExchangeRate } from '../lib/useExchangeRate'
import { useToast } from '../lib/useToast'
import { useBinanceCheck } from '../lib/useBinanceCheck'
import { purchaseSubscription, type PurchaseSubscriptionParams } from '../lib/contract'
import type { Phase2DataResponse } from '../types'
import { useWallet } from '../lib/useWallet'

// Import all images
import pinaiLogoBall from '../assets/images/pinai_logo_ball.png'
import purpleGrow from '../assets/images/phase2_live/purple_grow.png'
import pianiBanner from '../assets/images/piani_banner.png'

interface PlanDetails {
  title: string
  price?: string
  buttonText?: string
  isExpanded: boolean
}

export function Phase2DetailPage() {
  const navigate = useNavigate()
  const { updateRate } = useExchangeRate()
  const { toast, showError, hideToast } = useToast()
  const { checkBinanceAndExecute } = useBinanceCheck()
  const { address } = useWallet()
  const [phase2Data, setPhase2Data] = useState<Phase2DataResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const [plans, setPlans] = useState<Record<string, PlanDetails>>({
    premium: {
      title: 'Premium',
      price: '0.5 BNB',
      buttonText: 'Deposit 0.5 BNB',
      isExpanded: true
    },
    pro: {
      title: 'Pro',
      price: '1.5 BNB',
      buttonText: 'Deposit 1.5 BNB',
      isExpanded: true
    },
    free: {
      title: 'Free',
      price: '0 BNB',
      buttonText: 'Start for Free',
      isExpanded: true
    }
  })

  // 存储计划的动态数据
  const [planData, setPlanData] = useState<Record<string, { subscribers: number; estimateRewards: string }>>({
    premium: { subscribers: 0, estimateRewards: '0' },
    pro: { subscribers: 0, estimateRewards: '0' },
    free: { subscribers: 0, estimateRewards: '0' }
  })

  const [depositModal, setDepositModal] = useState({
    isOpen: false,
    amount: '',
    planType: ''
  })

  const [successModal, setSuccessModal] = useState(false)
  const [isProcessingTransaction, setIsProcessingTransaction] = useState(false)

  // 加载Zendesk Widget脚本
  useEffect(() => {
    const script = document.createElement('script')
    script.id = 'ze-snippet'
    script.src = 'https://static.zdassets.com/ekr/snippet.js?key=32dcc327-74dd-4859-a6a0-0924017a6dbb'
    script.async = true
    
    document.head.appendChild(script)
    
    // 清理函数：组件卸载时移除脚本
    return () => {
      const existingScript = document.getElementById('ze-snippet')
      if (existingScript) {
        existingScript.remove()
      }
    }
  }, [])

  console.log('phase2Data :>> ', phase2Data);
  // 加载 Phase2 数据
  useEffect(() => {
    const loadPhase2Data = async () => {
      if (!address) {
        showError("Please connect to binance wallet");
        return;
      }

      try {
        setIsLoading(true)
        setError(null)
        
        // 并行执行汇率更新和数据获取以提升性能
        const [, data] = await Promise.all([
          updateRate(),
          getPhase2Data(address)
        ])
        
        setPhase2Data(data)
        
        // 优化数据处理 - 批量更新状态
        setPlans(prev => {
          const updated = { ...prev }
          
          // 如果用户订阅了 Free 计划，将 premium 和 pro 默认收起来
          if (data.plan_type === 'Free') {
            updated.premium.isExpanded = false
            updated.pro.isExpanded = false
          }
          
          data.plans.forEach(plan => {
            const planKey = plan.plan_type.toLowerCase()
            if (updated[planKey]) {
              updated[planKey].price = plan.price === 0 ? 'Free' : `${plan.price} BNB`
              if (plan.price > 0) {
                updated[planKey].buttonText = `Deposit ${plan.price} BNB`
              }
            }
          })
          
          return updated
        })
        
        setPlanData(prev => {
          const updated = { ...prev }
          data.plans.forEach(plan => {
            const planKey = plan.plan_type.toLowerCase()
            if (updated[planKey]) {
              updated[planKey] = {
                subscribers: plan.subscribers,
                estimateRewards: plan.estimate_rewards
              }
            }
          })
          return updated
        })
        
      } catch (err) {
        console.error('Failed to load phase2 data:', err)
        if (err instanceof ApiError) {
          setError(`API错误: ${err.message}`)
        } else {
          setError('加载Phase2数据失败')
        }
      } finally {
        setIsLoading(false)
      }
    }

    loadPhase2Data()
  }, [updateRate, address, showError])

  const togglePlan = (planKey: string) => {
    setPlans(prev => ({
      ...prev,
      [planKey]: {
        ...prev[planKey],
        isExpanded: !prev[planKey].isExpanded
      }
    }))
  }

  const handleDepositClick = (amount: string, planType: string) => {
    checkBinanceAndExecute(() => {
    setDepositModal({
      isOpen: true,
      amount,
      planType
      })
    })
  }

  const handleCloseModal = () => {
    setDepositModal({
      isOpen: false,
      amount: '',
      planType: ''
    })
  }

  const handleConfirmDeposit = async () => {
    try {
      setIsProcessingTransaction(true)

      // 解析金额字符串，移除 " BNB" 后缀
      const cleanAmount = depositModal.amount.replace(' BNB', '').trim()
      
      // 准备合约调用参数
      const contractParams: PurchaseSubscriptionParams = {
        planType: depositModal.planType,
        amount: cleanAmount
      }

      console.log('开始购买订阅:', contractParams)

      // 调用合约
      const result = await purchaseSubscription(contractParams)

      if (result.success) {
        console.log('订阅购买成功:', result.hash)
        
        // 关闭充值弹窗并显示成功弹窗
        handleCloseModal()
        setSuccessModal(true)
        
        // 重新加载Phase2数据以更新UI状态
        if (!address) {
          showError("Please connect to binance wallet");
          return;
        }
        
        const updatedData = await getPhase2Data(address)
        setPhase2Data(updatedData)
        
        // 更新计划数据
        setPlanData(prev => {
          const updated = { ...prev }
          updatedData.plans.forEach(plan => {
            const planKey = plan.plan_type.toLowerCase()
            if (updated[planKey]) {
              updated[planKey] = {
                subscribers: plan.subscribers,
                estimateRewards: plan.estimate_rewards
              }
            }
          })
          return updated
        })
        
      } else {
        console.error('订阅购买失败:', result.error)
        showError(result.error || 'Transaction failed')
      }

    } catch (error) {
      console.error('处理交易时出错:', error)
      const errorMessage = error instanceof Error ? error.message : 'Transaction processing failed'
      showError(errorMessage)
    } finally {
      setIsProcessingTransaction(false)
    }
  }

  return (
    <div className="w-full min-h-screen bg-gradient-to-b from-[#F2FBFE] to-white">
      {/* 加载状态 */}
      {isLoading && (
        <div className="fixed inset-0 bg-white/80 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      )}
      
      {/* 错误提示 */}
      {error && (
        <div className="fixed top-4 left-4 right-4 bg-red-50 border border-red-200 rounded-lg p-3 z-40">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Toast 提示 */}
      <Toast
        type={toast.type}
        title={toast.title}
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
      
      <div className="flex flex-col px-5">
        {/* Header Section */}
        <div className="flex-shrink-0 relative">
          {/* Header */}
          <div className="flex-shrink-0 h-[60px] flex items-center mt-[10px]">
            <button 
              onClick={() => navigate(-1)}
              className="w-[30px] h-[25.71px] flex items-center justify-center"
            >
              <ChevronLeft className="w-6 h-6 text-gray-900" strokeWidth={2.5} />
            </button>
          </div>
          
          <div className="flex flex-col gap-2.5 pt-[5px] pb-6">
            <div className="font-kanit font-bold text-[#280D5E] text-[30px] sm:text-[30px] tracking-[0] leading-[32px] sm:leading-[36px]">
            <span className='font-medium text-[20px] sm:text-[20px] '> Exclusive Pre-TGE</span>
            <br/>
              PIN AI's 
            <br/>
              AI Mining Subscription
            </div>
            <p className="font-kanit font-medium text-[#796EAA] text-sm sm:text-base tracking-[0] leading-[20px] sm:leading-[22px]">
              Exclusively via Binance Keyless Wallet
            </p>
          </div>
        </div>

        {/* Live Campaign Card */}
        <div className="relative w-full h-[113px] bg-gray-900 rounded-[12px] border-[0.5px] border-solid border-[#CACFDB] flex flex-col justify-center overflow-hidden mb-6">
          <div className="flex flex-col w-full items-center">
            <div className="w-full overflow-hidden flex items-center justify-center">
              <img
                className="w-full object-cover object-center"
                alt="PIN AI Banner"
                src={pianiBanner}
              />
            </div>
            <div className="relative w-full h-[50px] mt-[-1px]">
              <div className={`relative w-full h-[50px] rounded-[0px_0px_12px_12px] ${
                phase2Data?.is_phase2_finished 
                  ? 'bg-[#7A6EAA]' 
                  : 'bg-gradient-to-r from-[#492286] via-[#8051D6] to-[#8051D6]'
              }`}>
                <p className={`absolute inset-0 flex items-center justify-center text-center font-kanit font-medium leading-[22px] whitespace-nowrap px-2 ${
                  phase2Data?.is_phase2_finished 
                    ? 'text-white' 
                    : 'text-[#F9AD58]'
                } text-lg`}>
                  {phase2Data?.is_phase2_finished 
                    ? 'Sale Finished'
                    : phase2Data?.plan_type !== "Inactive" 
                      ? 'Subscription Successful'
                      : 'LIVE NOW!'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Total Sale Card - 条件渲染 */}
        <div className="relative w-full bg-[#F9F9F9] border border-[#E5E7EC] rounded-[20px] p-4 mb-2.5">
          <div className="flex items-start gap-3">
            <img 
              src={pinaiLogoBall} 
              alt="Mining Machine Icon" 
              className="w-[59px] h-[59px] rounded-lg flex-shrink-0 object-cover shadow-card"
            />
            <div className="flex-1">
              <div className="font-kanit font-bold text-[#7746DB] text-xs sm:text-sm leading-[24px] sm:leading-[27px]">
                Total Allocation
              </div>
              <div className="font-kanit font-bold text-[#280D5E] text-lg sm:text-xl leading-[24px] sm:leading-[27px] break-words">
                {phase2Data ? formatNumberWithCommas(phase2Data.total_allocation) : '65,000,000'} PIN
              </div>
            </div>
          </div>
          
          {/* 条件显示不同的的总结信息 */}
          {phase2Data?.is_phase2_finished && phase2Data?.plan_type == "Inactive" ? (
            // Phase2 已结束
            <>
              <div className="flex w-full items-center justify-between mt-4">
                <div className="font-kanit font-medium text-[#796EAA] text-sm sm:text-[15px] leading-[20px] sm:leading-[22px]">
                  Total Participants
                </div>
                <div className="font-kanit font-medium text-[#280D5E] text-sm sm:text-[15px] text-right leading-[20px] sm:leading-[22px] break-words">
                  {formatNumberWithCommas(phase2Data?.total_subscribers || 0)}
                </div>
              </div>
              
              <div className="flex w-full items-center justify-between mt-2">
                <div className="font-kanit font-medium text-[#796EAA] text-sm sm:text-[15px] leading-[20px] sm:leading-[22px]">
                  &nbsp;&nbsp;&nbsp;&nbsp;Premium Plan
                </div>
                <div className="font-kanit font-medium text-[#280D5E] text-sm sm:text-[15px] text-right leading-[20px] sm:leading-[22px] break-words">
                  {formatNumberWithCommas(planData.premium.subscribers)}
                </div>
              </div>
              
              <div className="flex w-full items-center justify-between mt-2">
                <div className="font-kanit font-medium text-[#796EAA] text-sm sm:text-[15px] leading-[20px] sm:leading-[22px]">
                &nbsp;&nbsp;&nbsp;&nbsp;Pro Plan
                </div>
                <div className="font-kanit font-medium text-[#280D5E] text-sm sm:text-[15px] text-right leading-[20px] sm:leading-[22px] break-words">
                  {formatNumberWithCommas(planData.pro.subscribers)}
                </div>
              </div>

              <p className="font-kanit font-medium text-[#796EAA] text-[13px] leading-[18px] mt-4">
                This phase is complete—but the journey has just begun. Follow PIN AI and stay tuned for what's coming next.
              </p>
            </>
          ) : (
            // Phase2 未结束
            phase2Data?.plan_type !== "Inactive" ? (
              // 已订阅非Free计划：显示 Subscription Type 和 Subscription Amount
              <>
                <div className="flex w-full items-center justify-between mt-4">
                  <div className="font-kanit font-medium text-[#796EAA] text-[15px] leading-[22px]">
                    Subscription Type
                  </div>
                  <div className="font-kanit font-medium text-[#280D5E] text-[15px] text-right leading-[22px]">
                    {phase2Data?.plan_type}
                  </div>
                </div>
                
                <div className="flex w-full items-center justify-between mt-2">
                  <div className="font-kanit font-medium text-[#796EAA] text-[15px] leading-[22px]">
                    Subscription Amount
                  </div>
                  <div className="font-kanit font-medium text-[#280D5E] text-[15px] text-right leading-[22px]">
                    {phase2Data?.payment_amount || 0} BNB
                  </div>
                </div>

                <p className="font-kanit font-medium text-[#796EAA] text-[13px] leading-[18px] mt-4">
                  Dual Pool Access: Free Pool (up to 50%) + Premium Pool (≥ 50%)
                </p>
              </>
            ) : (
              // Free计划：显示 Project Duration、Total Subscribers 和 Total Committed
              <>
                <div className="flex w-full items-center justify-between mt-4">
                  <div className="font-kanit font-medium text-[#796EAA] text-[15px] leading-[22px]">
                    Project Duration
                  </div>
                  <div className="font-kanit font-medium text-[#280D5E] text-[15px] text-right leading-[22px]">
                    {phase2Data?.project_duration || '12 weeks'}
                  </div>
                </div>
                
                <div className="flex w-full items-center justify-between mt-2">
                  <div className="font-kanit font-medium text-[#796EAA] text-[15px] leading-[22px]">
                    Total Paid Subscribers
                  </div>
                  <div className="font-kanit font-medium text-[#280D5E] text-[15px] text-right leading-[22px]">
                    {formatNumberWithCommas(phase2Data?.total_committed || 0)} BNB
                  </div>
                </div>

                <p className="font-kanit font-medium text-[#796EAA] text-[13px] leading-[18px] mt-4">
                  Subscribe to PIN AI (Free / Premium / Pro) and earn PIN tokens by using your Personal AI, completing weekly tasks, and sharing on X.
                </p>
              </>
            )
          )}

          {/* 条件显示按钮 - 移到Total Sale Card外面 */}
          {phase2Data && (
            <>
              {phase2Data.is_phase2_finished ? (
                <CommonButton 
                  className="mb-2"
                  text="More Airdrops" 
                  onClick={() => window.open('https://www.pinai.io/', '_blank')}
                />
              ) : phase2Data.plan_type !== "Inactive" ? (
                <CommonButton 
                  className="mb-2"
                  text="My Mining Rewards" 
                  onClick={() => navigate(phase2Data.has_earnings ? '/check-allocation' : '/allocation-help')}
                />
              ) : null}
            </>
          )}
          
          
        </div>


        {/* Plans Section - 条件渲染 */}
        {phase2Data && !phase2Data.is_phase2_finished && (phase2Data.plan_type === "Free" || phase2Data.plan_type === "Inactive") && (
          <div className="flex flex-col gap-3.5 mb-2.5">

            {/* Premium Plan */}
            <div className="relative w-full bg-[#F9F9F9] border border-[#E5E7EC] rounded-[20px] overflow-hidden">
              <div className="p-3">
                <button 
                  onClick={() => togglePlan('premium')}
                  className="w-full flex items-center justify-between"
                >
                  <div className="font-kanit font-bold text-[#7E51DC] text-sm sm:text-base leading-[24px] sm:leading-[27px]">
                    Premium
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="font-kanit font-bold text-[#280D5E] text-sm sm:text-base leading-[24px] sm:leading-[27px]">
                      {plans.premium.price}
                    </div>
                    {plans.premium.isExpanded ? (
                      <ChevronDown className="w-[18px] h-[18px] text-[#280D5E]" />
                    ) : (
                      <ChevronRight className="w-[18px] h-[18px] text-[#280D5E]" />
                    )}
                  </div>
                </button>
                
                {plans.premium.isExpanded && (
                  <div className="mt-1 mb-4">
                    <ul className="font-kanit text-sm sm:text-[15px] leading-[20px] sm:leading-[22px] mb-4 list-disc list-inside space-y-1 [&>li]:pl-4">
                      <li className="font-medium text-[#796eaa]">
                        Eligible for Free + Paid Pool (2× 325M PIN)
                      </li>
                      <li>
                        <span className="font-medium text-[#796eaa]">AI Boost Multiplier: </span>
                        <span className="font-bold text-[#7746db] text-base sm:text-lg">5x</span>
                      </li>
                      <li className="text-sm font-medium text-[#796eaa]">
                        $30/month AI Credits × <span className="font-bold text-[#7746db] text-base sm:text-lg">12 months</span>
                      </li>
                      <li className="text-sm font-medium text-[#796eaa]">
                        Access premium AI features
                      </li>
                      <li className="text-sm font-medium text-[#796eaa]">
                        Early access to new features
                      </li>
                    </ul>
                    
                    {/* 分割线 */}
                    <div className="w-full h-px bg-[#796EAA]/50 my-[5px]"></div>
                    
                    <ul className="font-kanit text-sm sm:text-[15px] leading-[20px] sm:leading-[22px] list-disc list-inside space-y-1 [&>li]:pl-4">
                      <li className="font-medium text-[#796eaa]">
                        Total Premium Subscribers: <span className="font-bold text-[#7746db]">{formatNumberWithCommas(planData.premium.subscribers)}</span>
                      </li>
                      <li className="font-medium text-[#796eaa]">
                        Est. reward per Premium user: <span className="font-bold text-[#7746db]">{planData.premium.estimateRewards} PIN</span> token
                      </li>
                    </ul>
                  </div>
                )}
                {plans.premium.buttonText && (
                  <>
                    <button 
                      onClick={() => handleDepositClick(plans.premium.price || '0.5 BNB', 'Premium')}
                      className="w-full bg-primary-400 text-white font-kanit font-bold text-base py-3.5 rounded-xl shadow-soft mt-3"
                    >
                      {plans.premium.buttonText}
                    </button>
                    <p className="font-kanit font-semibold text-[#7746DB] text-base leading-[24px] text-center mt-3">
                      Best value for early AI miners
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Pro Plan */}
            <div className="relative w-full bg-[#F9F9F9] border border-[#E5E7EC] rounded-[20px] overflow-hidden">
              <div className="p-3">
                <button 
                  onClick={() => togglePlan('pro')}
                  className="w-full flex items-center justify-between"
                >
                  <div className="font-kanit font-bold text-[#7E51DC] text-base leading-[27px]">
                    {plans.pro.title}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="font-kanit font-bold text-[#280D5E] text-base leading-[27px]">
                      {plans.pro.price}
                    </div>
                    {plans.pro.isExpanded ? (
                      <ChevronDown className="w-[18px] h-[18px] text-[#280D5E]" />
                    ) : (
                      <ChevronRight className="w-[18px] h-[18px] text-[#280D5E]" />
                    )}
                  </div>
                </button>
                
                {plans.pro.isExpanded && (
                  <div className="mt-1 mb-4">
                    <ul className="font-kanit text-sm sm:text-[15px] leading-[20px] sm:leading-[22px] mb-4 list-disc list-inside space-y-1 [&>li]:pl-4">
                      <li className="font-medium text-[#796eaa]">
                        Eligible for Free + Paid Pool (2× 325M PIN)
                      </li>
                      <li>
                        <span className="font-medium text-[#796eaa]">AI Boost Multiplier: </span>
                        <span className="font-bold text-[#7746db] text-base sm:text-lg">10x</span>
                      </li>
                      <li className="text-sm font-medium text-[#796eaa]">
                        $30/month AI Credits × <span className="font-bold text-[#7746db] text-base sm:text-lg">48 months</span>
                      </li>
                      <li className="text-sm font-medium text-[#796eaa]">
                        Includes everything in Premium Plan
                      </li>
                      <li className="text-sm font-medium text-[#796eaa]">
                        Highest-tier AI models & access to future Lab-only features
                      </li>
                      <li className="text-sm font-medium text-[#796eaa]">
                        Priority access to advanced beta tools & experiments
                      </li>
                    </ul>
                    
                    {/* 分割线 */}
                    <div className="w-full h-px bg-[#796EAA]/50 my-[5px]"></div>
                    
                    <ul className="font-kanit text-sm sm:text-[15px] leading-[20px] sm:leading-[22px] list-disc list-inside space-y-1 [&>li]:pl-4">
                      <li className="font-medium text-[#796eaa]">
                        Total Pro Subscribers: <span className="font-bold text-[#7746db]">{formatNumberWithCommas(planData.pro.subscribers)}</span>
                      </li>
                      <li className="font-medium text-[#796eaa]">
                        Est. reward per Pro user: <span className="font-bold text-[#7746db]">{planData.pro.estimateRewards} PIN</span> token
                      </li>
                    </ul>
                  </div>
                )}
                {plans.pro.buttonText && (
                  <>
                    <button 
                      onClick={() => handleDepositClick(plans.pro.price || '1.5 BNB', 'Pro')}
                      className="w-full bg-primary-400 text-white font-kanit font-bold text-base py-3.5 rounded-xl shadow-soft mt-3"
                    >
                      {plans.pro.buttonText}
                    </button>
                    <p className="font-kanit font-semibold text-[#7746DB] text-base leading-[24px] text-center mt-3">
                      Maximum AI power + priority privileges
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Free Plan */}
            <div className="relative w-full bg-[#F9F9F9] border border-[#E5E7EC] rounded-[20px] overflow-hidden">
              <div className="p-3">
                <button 
                  onClick={() => togglePlan('free')}
                  className="w-full flex items-center justify-between"
                >
                  <div className="font-kanit font-bold text-[#7E51DC] text-base leading-[27px]">
                    {plans.free.title}
                  </div>
                  {plans.free.isExpanded ? (
                    <ChevronDown className="w-[18px] h-[18px] text-[#280D5E]" />
                  ) : (
                    <ChevronRight className="w-[18px] h-[18px] text-[#280D5E]" />
                  )}
                </button>
                
                {plans.free.isExpanded && (
                  <div className="mt-1 mb-4">
                    <ul className="font-kanit text-sm sm:text-[15px] leading-[20px] sm:leading-[22px] mb-4 list-disc list-inside space-y-1 [&>li]:pl-4">
                      <li className="font-medium text-[#796eaa]">
                        Eligible for Free Pool (325M PIN)
                      </li>
                      <li>
                        <span className="font-medium text-[#796eaa]">AI Boost Multiplier: </span>
                        <span className="font-bold text-[#7746db] text-base sm:text-lg">1x</span>
                      </li>
                      <li className="text-sm font-medium text-[#796eaa]">
                        Free Personal AI Builder
                      </li>
                      <li className="text-sm font-medium text-[#796eaa]">
                        Limited Deep Search Access
                      </li>
                      <li className="text-sm font-medium text-[#796eaa]">
                        Limited Web3 Agent Access
                      </li>
                      <li className="text-sm font-medium text-[#796eaa]">
                        Includes reserved seats from 3M+ Hi PIN users
                      </li>
                    </ul>
                    
                    {/* 分割线 */}
                    <div className="w-full h-px bg-[#796EAA]/50 my-[5px]"></div>
                    
                    <ul className="font-kanit text-sm sm:text-[15px] leading-[20px] sm:leading-[22px] mb-4 list-disc list-inside space-y-1 [&>li]:pl-4">
                      <li className="font-medium text-[#796eaa]">
                        Total Subscribers: <span className="font-bold text-[#7746db]">3M+</span>
                      </li>
                      <li className="font-medium text-[#796eaa]">
                        Est. reward per user: <span className="font-bold text-[#7746db]">{planData.free.estimateRewards} PIN</span> token
                      </li>
                    </ul>
                  </div>
                )}
                {plans.free.buttonText && phase2Data?.plan_type === "Inactive" && (
                  <>
                    <button 
                      onClick={() => handleDepositClick('0 BNB', 'Free')}
                      className="w-full bg-primary-400 text-white font-kanit font-bold text-base py-3.5 rounded-xl shadow-soft mt-3"
                    >
                      {plans.free.buttonText}
                    </button>
                    <p className="font-kanit font-semibold text-[#7746DB] text-base leading-[24px] text-center mt-3">
                      Basic AI capabilities
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PIN AI Description */}
        <div className="mb-6 ml-2.5 mt-2">
          <div className="flex items-center gap-2 mb-4">
            <img 
              src={purpleGrow} 
              alt="PIN AI" 
              className="w-[62px] h-[34px]"
            />
          </div>
          
          <p className="font-kanit font-medium text-[#7D73AC] text-sm leading-[20px] mb-4">
            PIN AI is the first fully decentralized Personal Intelligence Network, giving users full control and rewards for their data. Our self-custodial Data Wallet unifies Web2 and Web3 info, powering AI Assistants that deliver context-aware Agent Cards for life, finance, and crypto. At the protocol level, a decentralized marketplace matches user intents with competing AI agents, apps, and dApps—fueling a new economy of personalized services.
          </p>
          
          <div className="space-y-2">
            <p className="font-kanit font-medium text-[#7D73AC] text-sm leading-[22px]">
              Website: <a href="https://www.pinai.io/" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#7746DB] transition-colors">https://www.pinai.io/</a>
            </p>
            <p className="font-kanit font-medium text-[#7D73AC] text-sm leading-[22px]">
              X: <a href="https://x.com/PINAI_IO" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#7746DB] transition-colors">https://x.com/PINAI_IO</a>
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <FAQSection />

        {/* Bottom spacing for safe area */}
        <div className="h-[34px]" />
      </div>

      {/* Deposit Modal */}
      <DepositModal
        isOpen={depositModal.isOpen}
        onClose={handleCloseModal}
        amount={depositModal.amount}
        planType={depositModal.planType}
        onConfirm={handleConfirmDeposit}
        isProcessing={isProcessingTransaction}
      />

      {/* Transaction Success Modal */}
      <TransactionSuccessModal
        isOpen={successModal}
        onClose={() => setSuccessModal(false)}
      />

      {/* 开发环境下显示测试面板 import.meta.env.DEV */}
      { import.meta.env.DEV && (
        <Phase2TestPanel setPhase2Data={setPhase2Data} />
      )}
    </div>
  )
} 